import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { HelperText } from '@cbhq/cds-web/controls/HelperText';
import { VStack } from '@cbhq/cds-web/layout';
import { SectionHeader } from '@cbhq/cds-web/section-header/SectionHeader';
import { Text } from '@cbhq/cds-web/typography/Text';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { EntityAccessFooter } from '../components/InviteTray/EntityAccessFooter';
import { InviteEmailSection } from '../components/InviteTray/InviteEmailSection';
import { InviteTray } from '../components/InviteTray/InviteTray';
import { MultiLEProductAccessGroup } from '../components/InviteTray/MultiLEProductAccessGroup';
import { ProtectedRoleWarning } from '../components/InviteTray/ProtectedRoleWarning';
import { useInviteEmailField } from '../components/InviteTray/useInviteEmailField';
import { useProtectedRoleWarning } from '../components/InviteTray/useProtectedRoleWarning';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useInviteSend } from '../hooks/useInviteSend';
import { useLocalizedProducts } from '../hooks/useLocalizedProducts';
import { useManagePermissionsNow } from '../hooks/useManagePermissionsNow';
import { useMultiLEProductAccessEntities } from '../hooks/useMultiLEProductAccessEntities';
import { useMultiLEProductSelection } from '../hooks/useMultiLEProductSelection';
import { useRoles } from '../hooks/useRoles';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { useWorkspaceTeam } from '../hooks/useWorkspaceTeam';
import { canManageEntity } from '../utils/canManageMembers';
import type { MemberRole } from '../utils/constants';
import { hasGatedRole } from '../utils/stepUp';

const messages = defineMessages({
  accessHeading: {
    id: 'Team.inviteTray.accessHeading',
    defaultMessage: 'Access',
    description: 'Heading for the product access section of the invite tray',
  },
  inviteSuccess: {
    id: 'Team.multiLEInviteTray.inviteSuccess',
    defaultMessage: 'Invite sent',
    description: 'Toast shown after a multi legal entity invite is successfully sent',
  },
  inviteError: {
    id: 'Team.multiLEInviteTray.inviteError',
    defaultMessage: 'Error sending invite',
    description: 'Toast shown when sending a multi legal entity invite fails',
  },
  productAccessRequiredError: {
    id: 'Team.inviteTray.productAccessRequiredError',
    defaultMessage: 'You must select at least one product',
    description: 'Inline error shown when no product access has been selected on submit',
  },
});

type MultiLEInviteTrayParams = {
  onClose: () => void;
};

export function MultiLEInviteTray({ onClose }: MultiLEInviteTrayParams) {
  const { formatMessage } = useIntl();
  const { selectedOrganization } = useOrganization();
  const userProfile = useUserProfile();
  const currentUserEmail = userProfile?.email;
  const organizationId = selectedOrganization?.organizationId ?? '';

  const {
    members: workspaceMembers,
    pendingInvites,
    currentMember: currentUser,
  } = useWorkspaceTeam(organizationId);

  const {
    email,
    emailError,
    setEmailError,
    handleEmailChange,
    validate: validateEmail,
  } = useInviteEmailField({
    currentUserEmail,
    members: workspaceMembers,
    pendingInvites,
  });
  const [accessError, setAccessError] = useState<string | null>(null);
  const { rolesByProductPerEntity } = useRoles();

  const localizedProducts = useLocalizedProducts();

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  // IDFE-4022: an admin role still in its grace period doesn't grant manage controls.
  const managePermissionsNow = useManagePermissionsNow(currentUser, organizationId);

  // The tray lists every entity the user can see, but the backend rejects the
  // whole invite if it includes an entity the user can't manage. Those entities
  // render disabled and are excluded from selection and submission.
  const isEntityEditable = useCallback(
    (legalEntityId: string) => canManageEntity(currentUser, legalEntityId, managePermissionsNow),
    [currentUser, managePermissionsNow],
  );

  const manageableLegalEntityIds = useMemo(
    () => legalEntityIds.filter(isEntityEditable),
    [isEntityEditable, legalEntityIds],
  );

  const multiLEProductSelection = useMultiLEProductSelection({
    legalEntityIds: manageableLegalEntityIds,
    rolesByProductPerEntity,
  });
  const {
    getLegalEntityRoles,
    hasAnySelection,
    handleSelectAllEntities,
    handleDeselectAllEntities,
  } = multiLEProductSelection;

  const legalEntityRoles = useMemo(
    () => getLegalEntityRoles().filter(({ legalEntityId }) => isEntityEditable(legalEntityId)),
    [getLegalEntityRoles, isEntityEditable],
  );

  const submissionLegalEntityIds = useMemo(
    () => legalEntityRoles.map((le) => le.legalEntityId),
    [legalEntityRoles],
  );
  const submissionSelectedRoles = useMemo<MemberRole[]>(() => [], []);

  const entities = useMultiLEProductAccessEntities({
    legalEntities: selectedOrganization?.legalEntities ?? [],
    localizedProducts,
    rolesByProductPerEntity,
    isEntityEditable,
    selection: multiLEProductSelection,
  });

  useEffect(() => {
    if (hasAnySelection) {
      setAccessError(null);
    }
  }, [hasAnySelection]);

  const hasUnsavedInvite = useMemo(
    () => email.length > 0 || hasAnySelection,
    [email.length, hasAnySelection],
  );

  const invitedRoles = useMemo(
    () => legalEntityRoles.flatMap(({ roles }) => roles),
    [legalEntityRoles],
  );
  const isGatedMutation = useMemo(() => hasGatedRole(invitedRoles), [invitedRoles]);
  const protectedRoleWarning = useProtectedRoleWarning(legalEntityRoles);

  const { isSubmitting, sendInvite, stepUpModalProps } = useInviteSend({
    organizationId,
    email,
    selectedRoles: submissionSelectedRoles,
    legalEntityIds: submissionLegalEntityIds,
    legalEntityRoles,
    isGatedRoleMutation: isGatedMutation,
    messages,
    setEmailError,
    onClose,
  });

  const handleInvite = useCallback(() => {
    const nextEmailError = validateEmail();
    const nextAccessError =
      !hasAnySelection || submissionLegalEntityIds.length === 0
        ? formatMessage(messages.productAccessRequiredError)
        : null;

    setEmailError(nextEmailError);
    setAccessError(nextAccessError);

    if (nextEmailError != null || nextAccessError != null) {
      return;
    }

    sendInvite();
  }, [
    formatMessage,
    hasAnySelection,
    setEmailError,
    submissionLegalEntityIds.length,
    sendInvite,
    validateEmail,
  ]);

  const { trayRef, isAlertVisible, handleBeforeClose, handleKeepEditing, handleDiscard } =
    useUnsavedChangesGuard(hasUnsavedInvite);

  return (
    <>
      <InviteTray
        trayRef={trayRef}
        onClose={onClose}
        onInvite={handleInvite}
        beforeClose={handleBeforeClose}
        isSubmitting={isSubmitting}
      >
        <InviteEmailSection
          email={email}
          onEmailChange={handleEmailChange}
          errorMessage={emailError}
        />

        <VStack width="100%" gap={2}>
          <SectionHeader
            title={<Text font="headline">{formatMessage(messages.accessHeading)}</Text>}
            paddingX={0}
            paddingY={0}
          />
          <MultiLEProductAccessGroup
            entities={entities}
            footer={
              <EntityAccessFooter
                entities={entities}
                onSelectAll={handleSelectAllEntities}
                onDeselectAll={handleDeselectAllEntities}
              />
            }
          />
          {protectedRoleWarning != null && (
            <ProtectedRoleWarning
              roleName={protectedRoleWarning.roleName}
              gracePeriodMs={protectedRoleWarning.gracePeriodMs}
              context="invite"
            />
          )}
          {accessError != null && <HelperText color="fgNegative">{accessError}</HelperText>}
        </VStack>
      </InviteTray>
      <StepUpTwoFactorModal {...stepUpModalProps} />
      <UnsavedChangesAlert
        variant="invite"
        visible={isAlertVisible}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
